import { useState } from "react";
import { portfolioService } from "../../services/portfolioService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Download,
  Upload,
  Plus,
  AlertCircle,
  FileSpreadsheet,
  Landmark,
  X,
} from "lucide-react";
import { PlaidLinkPanel } from "./PlaidLinkPanel";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import type { Account, Position } from "../../interface/Account";
import { ACCOUNT_TYPES, OWNERSHIP_TYPES, SECTORS } from "../../utils/financialTaxonomy";
import type { AccountTypeKey } from "../../utils/financialTaxonomy";

interface AddAccountProps {
  open: boolean;
  onClose: () => void;
  onAccountsAdd: (accounts: Account[]) => void;
  userId?: string;
}

const generateAccountId = () => {
  return `account_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export function AddAccount({ open, onClose, onAccountsAdd, userId }: AddAccountProps) {
  const [csvUploadError, setCsvUploadError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("manual");
  const [isAddingManualAccount, setIsAddingManualAccount] = useState(false);

  const [currentAccount, setCurrentAccount] = useState({
    id: null as string | null,
    accountType: "BROKERAGE" as AccountTypeKey,
    ownershipType: "INDIVIDUAL" as keyof typeof OWNERSHIP_TYPES,
    accountName: "",
    accountNumber: "",
    custodian: "",
    accountNumberCustodian: "",
    balance: "",
    cashBalance: "",
    ytdReturn: "",
    status: "Active" as const,
    positions: [] as Array<{
      symbol: string;
      name: string;
      shares: number;
      avgCost: number;
      sector: string;
    }>,
  });

  const [manualPosition, setManualPosition] = useState({
    symbol: "",
    name: "",
    shares: "",
    avgCost: "",
    sector: "",
  });

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          setCsvUploadError("File appears to be empty or invalid");
          return;
        }

        const accountsMap: Record<string, any> = {};

        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(',').map(p => p.trim());
          if (parts.length >= 9) {
            const accountKey = parts[0] + "_" + parts[2];

            if (!accountsMap[accountKey]) {
              accountsMap[accountKey] = {
                id: null,
                accountName: parts[0],
                accountType: (parts[1] || "BROKERAGE") as AccountTypeKey,
                accountNumber: generateAccountId(),
                ownershipType: (parts[3] || "INDIVIDUAL") as keyof typeof OWNERSHIP_TYPES,
                custodian: "",
                accountNumberCustodian: "",
                status: "Active" as const,
                source: "csv" as const,
                balance: 0,
                cashBalance: 0,
                ytdReturn: 0,
                contributions: 0,
                openedDate: new Date().toISOString().split('T')[0],
                positions: [],
              };
            }

            accountsMap[accountKey].positions.push({
              symbol: parts[4],
              name: parts[5],
              shares: parseFloat(parts[6]) || 0,
              avgCost: parseFloat(parts[7]) || 0,
              price: parseFloat(parts[7]) || 0,
              value: (parseFloat(parts[6]) || 0) * (parseFloat(parts[7]) || 0),
              change: 0,
              totalReturn: 0,
              sector: parts[8] || "Diversified",
            });
          }
        }

        const parsedAccounts = Object.values(accountsMap) as Account[];
        if (parsedAccounts.length === 0) {
          setCsvUploadError("No valid accounts found in file");
          return;
        }

        const newAccounts: Account[] = [];
        for (const account of parsedAccounts) {
          if (userId) {
            const created = await portfolioService.createPortfolio(userId, account);
            newAccounts.push(created);
          } else {
            newAccounts.push(account);
          }
        }

        onAccountsAdd(newAccounts);
        setCsvUploadError(null);
        onClose();
      } catch (error) {
        setCsvUploadError(error instanceof Error ? error.message : "Error parsing file. Please check the format.");
      }
    };
    reader.readAsText(file);
  };

  const handleAddPosition = () => {
    if (!manualPosition.symbol || !manualPosition.shares || !manualPosition.avgCost) {
      return;
    }

    const newPosition = {
      symbol: manualPosition.symbol.toUpperCase(),
      name: manualPosition.name || manualPosition.symbol,
      shares: parseFloat(manualPosition.shares),
      avgCost: parseFloat(manualPosition.avgCost),
      sector: manualPosition.sector || "Diversified",
    };

    setCurrentAccount({
      ...currentAccount,
      positions: [...currentAccount.positions, newPosition],
    });

    setManualPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" });
  };

  const handleRemovePosition = (index: number) => {
    setCurrentAccount({
      ...currentAccount,
      positions: currentAccount.positions.filter((_, i) => i !== index),
    });
  };

  const handleSaveAccount = async () => {
    if (!currentAccount.accountName || currentAccount.positions.length === 0) {
      setUploadError("Please provide account name and at least one position");
      return;
    }

    const accountToSave: Account = {
      id: null,
      accountType: currentAccount.accountType,
      ownershipType: currentAccount.ownershipType,
      accountName: currentAccount.accountName,
      accountNumber: generateAccountId(),
      custodian: currentAccount.custodian,
      accountNumberCustodian: currentAccount.accountNumberCustodian,
      balance: parseFloat(currentAccount.balance) || 0,
      cashBalance: parseFloat(currentAccount.cashBalance) || 0,
      ytdReturn: parseFloat(currentAccount.ytdReturn) || 0,
      contributions: 0,
      status: "Active",
      source: "manual",
      openedDate: new Date().toISOString().split('T')[0],
      positions: currentAccount.positions.map((pos) => ({
        id: "",
        symbol: pos.symbol,
        name: pos.name,
        shares: pos.shares,
        avgCost: pos.avgCost,
        price: pos.avgCost,
        value: pos.shares * pos.avgCost,
        change: 0,
        totalReturn: 0,
        sector: pos.sector,
      })),
    };

    try {
      let savedAccount: Account;
      if (userId) {
        // createPortfolio expects the userId first, then the account object
        const response = await portfolioService.createPortfolio(userId, accountToSave);
        savedAccount = { ...accountToSave, ...response };
      } else {
        savedAccount = accountToSave;
      }

      onAccountsAdd([savedAccount]);
      setIsAddingManualAccount(false);
      setCurrentAccount({
        id: null,
        accountType: "BROKERAGE",
        ownershipType: "INDIVIDUAL",
        accountName: "",
        accountNumber: "",
        balance: "",
        cashBalance: "",
        ytdReturn: "",
        status: "Active",
        positions: [],
        custodian: "",
        accountNumberCustodian: "",
      });
      setUploadError(null);
      onClose();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Failed to create account");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setCsvUploadError(null);
          setUploadError(null);
          setActiveTab("manual");
        }
      }}
    >
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => {
          if (activeTab === "plaid") {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (activeTab === "plaid") {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Upload Your Portfolio</DialogTitle>
          <DialogDescription>
            Add your accounts and holdings by uploading a CSV file or entering details manually
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-1 gap-1 p-1 sm:grid-cols-3">
            <TabsTrigger
              value="csv"
              className="h-auto min-w-0 items-center justify-center gap-2 whitespace-normal py-2 text-center leading-tight"
              disabled
            >
              <Upload className="h-4 w-4 shrink-0" />
              <span className="min-w-0">Upload CSV (Coming Soon)</span>
            </TabsTrigger>
            <TabsTrigger
              value="manual"
              className="h-auto min-w-0 items-center justify-center gap-2 whitespace-normal py-2 text-center leading-tight"
            >
              <Plus className="h-4 w-4 shrink-0" />
              <span className="min-w-0">Add Manually</span>
            </TabsTrigger>
            <TabsTrigger
              value="plaid"
              className="h-auto min-w-0 items-center justify-center gap-2 whitespace-normal py-2 text-center leading-tight"
            >
              <Landmark className="h-4 w-4 shrink-0" />
              <span className="min-w-0">Link Bank Account</span>
            </TabsTrigger>
          </TabsList>

          {/* CSV Upload Tab */}
          <TabsContent value="csv" className="space-y-4 mt-4">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-sm font-medium">Upload Portfolio CSV</h3>
              <a
                href="/sample-portfolio-upload.csv"
                download
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Download className="h-3 w-3" />
                Download Sample CSV
              </a>
            </div>

            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <Label htmlFor="csv-upload" className="cursor-pointer">
                <div className="space-y-2">
                  <p>Click to upload or drag and drop</p>
                  <p className="text-sm text-muted-foreground">
                    CSV file with all your accounts and positions
                  </p>
                </div>
                <Input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleCsvUpload}
                />
              </Label>
            </div>

            {csvUploadError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{csvUploadError}</span>
              </div>
            )}

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">Sample CSV Data Format</p>
              <div className="bg-background rounded-lg overflow-hidden border">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium whitespace-nowrap border-r">Account Name</th>
                        <th className="text-left p-2 font-medium whitespace-nowrap border-r">Account Type</th>
                        <th className="text-left p-2 font-medium whitespace-nowrap border-r">Account #</th>
                        <th className="text-left p-2 font-medium whitespace-nowrap border-r">Ownership</th>
                        <th className="text-left p-2 font-medium whitespace-nowrap border-r">Symbol</th>
                        <th className="text-left p-2 font-medium whitespace-nowrap border-r">Name</th>
                        <th className="text-right p-2 font-medium whitespace-nowrap border-r">Shares</th>
                        <th className="text-right p-2 font-medium whitespace-nowrap border-r">Avg Cost</th>
                        <th className="text-left p-2 font-medium whitespace-nowrap">Sector</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="hover:bg-muted/30">
                        <td className="p-2 whitespace-nowrap border-r">Main Investment Account</td>
                        <td className="p-2 whitespace-nowrap border-r">BROKERAGE</td>
                        <td className="p-2 whitespace-nowrap border-r">****6789</td>
                        <td className="p-2 whitespace-nowrap border-r">INDIVIDUAL</td>
                        <td className="p-2 whitespace-nowrap font-medium border-r">AAPL</td>
                        <td className="p-2 whitespace-nowrap border-r">Apple Inc.</td>
                        <td className="p-2 text-right whitespace-nowrap border-r">500</td>
                        <td className="p-2 text-right whitespace-nowrap border-r">150.00</td>
                        <td className="p-2 whitespace-nowrap">Technology</td>
                      </tr>
                      <tr className="hover:bg-muted/30">
                        <td className="p-2 whitespace-nowrap border-r">Roth IRA - Primary</td>
                        <td className="p-2 whitespace-nowrap border-r">ROTH_IRA</td>
                        <td className="p-2 whitespace-nowrap border-r">****3421</td>
                        <td className="p-2 whitespace-nowrap border-r">INDIVIDUAL</td>
                        <td className="p-2 whitespace-nowrap font-medium border-r">VTI</td>
                        <td className="p-2 whitespace-nowrap border-r">Vanguard Total Stock Market ETF</td>
                        <td className="p-2 text-right whitespace-nowrap border-r">450</td>
                        <td className="p-2 text-right whitespace-nowrap border-r">210.00</td>
                        <td className="p-2 whitespace-nowrap">Diversified</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Expected CSV columns: Account Name, Account Type, Account Number, Ownership Type, Symbol, Name, Shares, Avg Cost, Sector</p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Manual Entry Tab */}
          <TabsContent value="manual" className="space-y-6 mt-4">
            {!isAddingManualAccount ? (
              <div className="text-center py-8">
                <div className="h-16 w-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                  <Plus className="h-8 w-8" />
                </div>
                <h3 className="text-lg mb-2">Add Your Account</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter account details and positions manually
                </p>
                <Button onClick={() => setIsAddingManualAccount(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Account
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Account Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Account Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="accountName">Account Name *</Label>
                      <Input
                        id="accountName"
                        placeholder="Main Investment Account"
                        value={currentAccount.accountName}
                        onChange={(e) =>
                          setCurrentAccount({ ...currentAccount, accountName: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountNumber">Account Number</Label>
                      <Input
                        id="accountNumber"
                        placeholder="Auto-generated"
                        value={currentAccount.accountNumber}
                        disabled
                        className="bg-muted"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custodian">Custodian</Label>
                      <Input
                        id="custodian"
                        placeholder="Fidelity"
                        value={currentAccount.custodian}
                        onChange={(e) =>
                          setCurrentAccount({ ...currentAccount, custodian: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountNumberCustodian">Custodian Account #</Label>
                      <Input
                        id="accountNumberCustodian"
                        placeholder="XYZ123"
                        value={currentAccount.accountNumberCustodian}
                        onChange={(e) =>
                          setCurrentAccount({ ...currentAccount, accountNumberCustodian: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountType">Account Type *</Label>
                      <Select
                        value={currentAccount.accountType}
                        onValueChange={(value) =>
                          setCurrentAccount({ ...currentAccount, accountType: value as AccountTypeKey })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ACCOUNT_TYPES).map(([key, value]) => (
                            <SelectItem key={key} value={key}>
                              {value.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ownershipType">Ownership Type *</Label>
                      <Select
                        value={currentAccount.ownershipType}
                        onValueChange={(value) =>
                          setCurrentAccount({ ...currentAccount, ownershipType: value as keyof typeof OWNERSHIP_TYPES })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select ownership" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(OWNERSHIP_TYPES).map(([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="balance">Current Balance</Label>
                      <Input
                        id="balance"
                        type="number"
                        placeholder="100000"
                        value={currentAccount.balance}
                        onChange={(e) =>
                          setCurrentAccount({ ...currentAccount, balance: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cashBalance">Cash Balance</Label>
                      <Input
                        id="cashBalance"
                        type="number"
                        placeholder="50000"
                        value={currentAccount.cashBalance}
                        onChange={(e) =>
                          setCurrentAccount({ ...currentAccount, cashBalance: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ytdReturn">YTD Return (%)</Label>
                      <Input
                        id="ytdReturn"
                        type="number"
                        step="0.1"
                        placeholder="12.5"
                        value={currentAccount.ytdReturn}
                        onChange={(e) =>
                          setCurrentAccount({ ...currentAccount, ytdReturn: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Positions */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Add Positions</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="symbol">Symbol *</Label>
                      <Input
                        id="symbol"
                        placeholder="AAPL"
                        value={manualPosition.symbol}
                        onChange={(e) =>
                          setManualPosition({ ...manualPosition, symbol: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name">Company Name</Label>
                      <Input
                        id="name"
                        placeholder="Apple Inc"
                        value={manualPosition.name}
                        onChange={(e) =>
                          setManualPosition({ ...manualPosition, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sector">Sector</Label>
                      <Select
                        value={manualPosition.sector}
                        onValueChange={(value) =>
                          setManualPosition({ ...manualPosition, sector: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sector" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTORS.map((sector) => (
                            <SelectItem key={sector} value={sector}>
                              {sector}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shares">Shares *</Label>
                      <Input
                        id="shares"
                        type="number"
                        placeholder="100"
                        value={manualPosition.shares}
                        onChange={(e) =>
                          setManualPosition({ ...manualPosition, shares: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="avgCost">Avg Cost (per share) *</Label>
                      <Input
                        id="avgCost"
                        type="number"
                        step="0.01"
                        placeholder="150.00"
                        value={manualPosition.avgCost}
                        onChange={(e) =>
                          setManualPosition({ ...manualPosition, avgCost: e.target.value })
                        }
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={handleAddPosition}
                        disabled={
                          !manualPosition.symbol ||
                          !manualPosition.shares ||
                          !manualPosition.avgCost
                        }
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Position
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Positions List */}
                {currentAccount.positions.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">
                      Positions ({currentAccount.positions.length})
                    </h3>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              <th className="text-left p-2">Symbol</th>
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Sector</th>
                              <th className="text-right p-2">Shares</th>
                              <th className="text-right p-2">Avg Cost</th>
                              <th className="text-right p-2">Total</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentAccount.positions.map((position, index) => (
                              <tr key={index} className="border-t">
                                <td className="p-2">{position.symbol}</td>
                                <td className="p-2 text-muted-foreground">{position.name}</td>
                                <td className="p-2">
                                  <Badge variant="outline" className="text-xs">
                                    {position.sector}
                                  </Badge>
                                </td>
                                <td className="p-2 text-right">{position.shares}</td>
                                <td className="p-2 text-right">${position.avgCost.toFixed(2)}</td>
                                <td className="p-2 text-right">
                                  ${(position.shares * position.avgCost).toFixed(2)}
                                </td>
                                <td className="p-2">
                                  <button
                                    onClick={() => handleRemovePosition(index)}
                                    className="text-muted-foreground hover:text-destructive"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {uploadError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAddingManualAccount(false);
                      setCurrentAccount({
                        id: null,
                        accountType: "BROKERAGE",
                        ownershipType: "INDIVIDUAL",
                        accountName: "",
                        accountNumber: "",
                        custodian: "",
                        accountNumberCustodian: "",
                        balance: "",
                        cashBalance: "",
                        ytdReturn: "",
                        status: "Active",
                        positions: [],
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveAccount} className="flex-1">
                    Save Account
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Link Bank (Plaid) Tab */}
          <TabsContent value="plaid" className="space-y-4 mt-4">
            <PlaidLinkPanel
              open={open}
              userId={userId}
              onAccountsAdd={onAccountsAdd}
              onClose={onClose}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
